import { Alert, Card, Col, Divider, Row } from 'antd';
import { Formik } from 'formik';
import { Form, FormItem, Input, SubmitButton } from 'formik-antd';
import moment from 'moment-timezone';
import { useIntl } from 'react-intl';

import styles from '../index.module.less';

import { IntlMessage, LocalePicker, Logo } from '$HVUI';
import { adjustCaretAfterEdit } from '$Utils';
import { normalizeIntlDate } from '$Validation/Normalizer';
import yup from '$Validation/yup';

const { date, object, string, lazy } = yup;

const ConfirmIdentity = ({
    error,
    handleSubmit,
    initialValues,
    updateIntl
}) => {
    const intl = useIntl();
    const validate = values => {
        const { birthdate, first_name, last_name } = values;
        if (!birthdate || !first_name || !last_name) {
            //returning hidden error prevents form submission.
            return { hiddenError: undefined };
        }
    };

    const intialize = initial => `${initial}___`;
    const firstInitials = initialValues?.first_name?.map(intialize).join(' ');
    const lastInitials = initialValues?.last_name?.map(intialize).join(' ');

    //Just match first initial for each name, don't worry about multiple
    const firstMatch = initialValues?.first_name?.[0]?.charAt(0)?.toUpperCase();
    const lastMatch = initialValues?.last_name?.[0]?.charAt(0)?.toUpperCase();

    const validationSchema = object().shape({
        first_name: lazy(() =>
            string()
                .validateInitial(
                    {
                        label: intl.formatMessage({
                            id: 'id-challenge.first-must-start-with',
                            defaultMessage: 'First name must start with '
                        })
                    },
                    firstMatch
                )
                .required(
                    intl.formatMessage({
                        id: 'id-challenge.required',
                        defaultMessage: 'Required'
                    })
                )
        ),
        last_name: lazy(() =>
            string()
                .validateInitial(
                    {
                        label: intl.formatMessage({
                            id: 'id-challenge.last-must-start-with',
                            defaultMessage: 'Last name must start with '
                        })
                    },
                    lastMatch
                )
                .required(
                    intl.formatMessage({
                        id: 'id-challenge.required',
                        defaultMessage: 'Required'
                    })
                )
        ),
        birthdate: lazy(() =>
            date()
                .transform(function (value, originalValue) {
                    if (this.isType(value)) return value;

                    // failed default parsing. try different formats
                    value = moment(originalValue, 'DD/MM/YYYY');

                    return value.isValid() ? value.toDate() : new Date('');
                })
                .typeError(
                    intl.formatMessage({
                        id: 'id-challenge.date-of-birth-must-follow-the-format',
                        defaultMessage:
                            'Date of birth must follow the format MM/DD/YYYY (e.g., 12/31/1990 for December 31, 1990)'
                    })
                )
                .validBirthday(
                    0,
                    intl.formatMessage({
                        id: 'id-challenge.date-cannot-be-in-the-future',
                        defaultMessage: 'Date cannot be in the future'
                    }),
                    intl.formatMessage({
                        id: 'id-challenge.must-be-less-than-years-old',
                        defaultMessage: 'Must be less than 123 years old'
                    })
                )
                .required(
                    intl.formatMessage({
                        id: 'id-challenge.required',
                        defaultMessage: 'Required'
                    })
                )
        )
    });

    // 'Must be less than 123 years old / Debe tener menos de 123 años'
    // 'Date cannot be in the future / La fecha no puede ser en el futuro'
    return (
        <div className={styles['wrapper']}>
            <Card
                className={[
                    styles['card'],
                    styles['identity-confirmation']
                ].join(' ')}
            >
                <Row>
                    <Col span={12}>
                        <Logo style={{ width: '100%' }} />
                    </Col>
                    <Col span={12} />
                </Row>
                <Divider />
                <LocalePicker updateIntl={updateIntl} />
                <h1>
                    <IntlMessage
                        id="id-challenge.please-enter-your-full-first-name-last"
                        defaultMessage="Please enter your full first name, last name, and date
                        of birth to continue."
                    />
                </h1>
                <p className={styles['lead']}>
                    <IntlMessage
                        id="id-challenge.here-are-some-helpful-notes"
                        defaultMessage="Here are some helpful notes:"
                    />
                </p>
                <ul>
                    <li>
                        <IntlMessage
                            id="id-challenge.all-fields-are-required"
                            defaultMessage="All fields are required."
                        />
                    </li>
                    <li>
                        <IntlMessage
                            id="id-challenge.when-entering-your-date-of-birth-please"
                            defaultMessage="When entering your date of birth,
                            please use the format of MM/DD/YYYY (e.g., If you
                            were born on December 31, 1990 you would enter
                            12/31/1990)."
                        />
                    </li>
                    <li>
                        <IntlMessage
                            id="id-challenge.this-notification-is-associated-with-the-person"
                            defaultMessage="This notification is associated
                            with the person whose initials are provided below."
                        />
                    </li>
                </ul>
                <p className={styles['initials-container']}>
                    <span className={styles['initials']}>
                        <IntlMessage
                            id="id-challenge.first"
                            defaultMessage="First:"
                        />{' '}
                        <strong>{firstInitials}</strong>
                    </span>
                    <span className={styles['initials']}>
                        <IntlMessage
                            id="id-challenge.last"
                            defaultMessage="Last:"
                        />{' '}
                        <strong>{lastInitials}</strong>
                    </span>
                </p>
                <Divider />
                <Formik
                    onSubmit={handleSubmit}
                    validate={validate}
                    validationSchema={validationSchema}
                    initialValues={{}}
                >
                    {({ setFieldValue }) => (
                        <Form
                            colon={false}
                            labelAlign="left"
                            labelCol={{ span: 6 }}
                        >
                            <FormItem
                                name="first_name"
                                label={
                                    <IntlMessage
                                        id="id-challenge.first-name"
                                        defaultMessage="First Name:"
                                    />
                                }
                            >
                                <Input
                                    size="large"
                                    name="first_name"
                                    placeholder={firstInitials}
                                />
                            </FormItem>
                            <FormItem
                                name="last_name"
                                label={
                                    <IntlMessage
                                        id="id-challenge.last-name"
                                        defaultMessage="Last Name:"
                                    />
                                }
                            >
                                <Input
                                    size="large"
                                    name="last_name"
                                    placeholder={lastInitials}
                                />
                            </FormItem>
                            <FormItem
                                name="birthdate"
                                label={
                                    <IntlMessage
                                        id="id-challenge.date-of-birth"
                                        defaultMessage="Date of Birth:"
                                    />
                                }
                            >
                                <Input
                                    size="large"
                                    name="birthdate"
                                    placeholder={intl.formatMessage({
                                        id: 'id-challenge.date-of-birth-format',
                                        defaultMessage: 'MM/DD/YYYY'
                                    })}
                                    onChange={e => {
                                        let value = e.target.value;
                                        value = normalizeIntlDate(value);
                                        setFieldValue('birthdate', value);
                                        adjustCaretAfterEdit(e.target);
                                    }}
                                />
                            </FormItem>
                            {error && (
                                <Alert
                                    className={styles['error-message']}
                                    type="error"
                                    message={error}
                                />
                            )}
                            <Row
                                style={{
                                    justifyContent: 'center',
                                    marginTop: '16px'
                                }}
                            >
                                <SubmitButton size="large" type="primary">
                                    <IntlMessage
                                        id="id-challenge.continue"
                                        defaultMessage="Continue"
                                    />
                                </SubmitButton>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Card>
        </div>
    );
};

export default ConfirmIdentity;
